/**
 * useOnboarding — el recorrido guiado de 5 pasos, visto desde el cliente.
 *
 * Dos fuentes, a propósito:
 *
 * - **El estado** viaja dentro de `['gbusiness','me']`, que el SPA ya pide en
 *   cada carga. Así el stepper se pinta sin una petición extra y las páginas
 *   donde no hay card no pagan nada.
 * - **Los datos de cada paso** se piden de uno en uno (`?step=N`) y solo en la
 *   página de ese paso. Traerlos todos costaría, en cada render, un Health
 *   Score completo y un documento de escaneo de 40 KB para rellenar cuatro
 *   cards que nadie está mirando.
 *
 * **«Guardar y seguir luego» NO cierra el recorrido.** Esconderlo dura lo que
 * la sesión y por eso vive en `sessionStorage`: el servidor solo apunta la
 * huella (`snoozed_at`, `snooze_count`) para poder medir cuánta gente lo
 * pospone. Para volver antes de que acabe la sesión está `useOnboardingResume`,
 * que es lo que pinta «Volver al recorrido» en el menú de usuario.
 *
 * **La X sí lo cierra, y para siempre.** Es la otra salida y no tiene vuelta:
 * el estado pasa a `dismissed` en Mongo, así que el recorrido no reaparece ni
 * cambiando de navegador o de móvil. La marca local que se escribe además
 * (`localStorage`) no es la que manda —manda el servidor—, solo evita que la
 * tarjeta resucite en el refetch siguiente si el POST se pierde.
 *
 * El avance se manda de forma optimista —el stepper tiene que moverse en el
 * momento— pero manda el servidor: su respuesta puede traer un paso MAYOR del
 * que pedimos, porque el avance es monótono y el usuario puede navegar hacia
 * atrás. Mismo patrón que `Reviews.jsx:markTourSeen`.
 */
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getGbusinessMe,
  getGbusinessOnboarding,
  setGbusinessOnboarding,
} from '@/api/gbusinessService';

export const ME_KEY = ['gbusiness', 'me'];
export const ONBOARDING_KEY = ['gbusiness', 'onboarding'];

/** Orden del recorrido. Espejo de `STEPS` en `gbusiness/services/onboarding.py`. */
export const STEP_PAGES = [
  { key: 'reviews', order: 1, page: '/Reviews' },
  { key: 'metrics', order: 2, page: '/GMBMetrics' },
  { key: 'health', order: 3, page: '/GMBHealthScore' },
  { key: 'geogrid', order: 4, page: '/GMBGeoGrid' },
  { key: 'automations', order: 5, page: '/Automations' },
];
export const TOTAL_STEPS = STEP_PAGES.length;

const OPEN = new Set(['pending', 'in_progress']);

/** ¿A qué paso corresponde esta ruta? `null` fuera del recorrido. */
export function stepForPath(pathname) {
  const hit = STEP_PAGES.find(
    (s) => (pathname || '').toLowerCase().startsWith(s.page.toLowerCase()),
  );
  return hit ? hit.order : null;
}

/**
 * El paso de la PÁGINA en la que estamos, que no es el mismo que el paso
 * guardado.
 *
 * La distinción es la que faltaba: `step` es la posición más lejana alcanzada y
 * es monótona porque de ella cuelga el embudo de admin, pero el recorrido tiene
 * que enseñarse allí donde el usuario ESTÁ. Sin esto, entrar en Reseñas con el
 * cursor en el 4 dejaba el punto naranja clavado en el Escáner SEO y la card de
 * Reseñas sin aparecer: el usuario navegaba, y el recorrido no le seguía.
 */
export function useOnboardingHere() {
  const { pathname } = useLocation();
  return useMemo(() => stepForPath(pathname), [pathname]);
}

// `sessionStorage` y no `localStorage`: el compromiso es «hasta la próxima
// sesión», así que tiene que morir al cerrar la pestaña. Con `localStorage`
// aparcarlo una vez lo escondería para siempre, que es justo lo que NO se pidió.
const SNOOZE_KEY = 'onboardingSnoozed';

// TERMINAR es definitivo, así que va en `localStorage` y no en `sessionStorage`:
// tiene que sobrevivir al cierre de la pestaña. Y guarda la VERSIÓN del
// recorrido, no un `'1'`: subir `ONBOARDING_VERSION` es la forma documentada de
// reabrirlo para todo el mundo, y una marca sin versión dejaría a quien terminó
// el recorrido de hoy sin ver nunca el de mañana.
const COMPLETED_KEY = 'onboardingCompleted';

// ABANDONAR es igual de definitivo que terminar, así que sigue el mismo patrón
// y por los mismos motivos: `localStorage` (sobrevive al cierre de la pestaña)
// y sellado con la VERSIÓN del recorrido (subir `ONBOARDING_VERSION` lo reabre
// para todo el mundo, también para quien cerró el anterior).
//
// Va en una clave APARTE y no reusando la de terminado, aunque las dos escondan
// la tarjeta: `useOnboardingHeal` tiene que saber cuál de las dos acciones
// reintentar contra el servidor, y confundirlas apuntaría como completado un
// recorrido que el usuario abandonó — justo el número que hay que vigilar.
const DISMISSED_KEY = 'onboardingDismissed';

const listeners = new Set();

function readRaw(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    // Safari en privado, o cookies bloqueadas: se prefiere enseñar la card a
    // reventar. Como mucho el usuario la vuelve a colapsar.
    return null;
  }
}

function writeRaw(storage, key, value) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch { /* ver `readRaw` */ }
  listeners.forEach((fn) => fn());
}

const writeSnoozed = () => writeRaw(sessionStorage, SNOOZE_KEY, '1');
const clearSnoozed = () => writeRaw(sessionStorage, SNOOZE_KEY, null);
const writeCompleted = (version) => writeRaw(localStorage, COMPLETED_KEY, String(version ?? 0));
const writeDismissed = (version) => writeRaw(localStorage, DISMISSED_KEY, String(version ?? 0));

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// El snapshot es una CADENA y no un objeto: `useSyncExternalStore` compara por
// identidad, y devolver un objeto nuevo en cada lectura sería un bucle infinito
// de renders.
function readMarks() {
  return [
    readRaw(sessionStorage, SNOOZE_KEY) === '1' ? 1 : 0,
    readRaw(localStorage, COMPLETED_KEY) || '',
    readRaw(localStorage, DISMISSED_KEY) || '',
  ].join(':');
}

const parseVersion = (raw) => {
  if (raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

/**
 * Las tres marcas locales: aparcado en esta sesión, y terminado o abandonado en
 * este navegador.
 */
function useLocalMarks() {
  const snap = useSyncExternalStore(subscribe, readMarks, () => '0::');
  return useMemo(() => {
    const [snoozedRaw, completedRaw, dismissedRaw] = snap.split(':');
    return {
      snoozed: snoozedRaw === '1',
      completedVersion: parseVersion(completedRaw),
      dismissedVersion: parseVersion(dismissedRaw),
    };
  }, [snap]);
}

/**
 * Estado del recorrido, sin datos de pasos. Barato: reutiliza `me`.
 *
 * @returns {{active: boolean, step: number, status: string, isLoading: boolean}}
 */
export function useOnboardingState() {
  const { data: me, isLoading } = useQuery({
    queryKey: ME_KEY,
    queryFn: getGbusinessMe,
    retry: false,
  });
  const { snoozed, completedVersion, dismissedVersion } = useLocalMarks();

  const ob = me?.onboarding || null;
  const status = ob?.status || 'not_eligible';

  // Terminado en ESTE navegador, para la versión que está corriendo ahora.
  // Manda sobre lo que diga el servidor porque puede no haberse enterado: el
  // POST de «Terminar» pudo caer (un 429 del throttle de 60/hora, la red), y
  // sin esta marca el recorrido resucitaba en el siguiente refetch delante de
  // alguien que ya lo había dado por acabado.
  //
  // La comparación es `>=` y no `===`: una marca de una versión ANTERIOR no
  // puede tapar un recorrido nuevo, que es justo lo que se busca al subir
  // `ONBOARDING_VERSION`.
  const sealed = (mark) => mark !== null && ob?.version != null && mark >= ob.version;
  const completedHere = sealed(completedVersion);
  // Abandonado con la X en este navegador. Se separa de `completedHere` porque
  // `useOnboardingHeal` tiene que reintentar la acción CORRECTA: un abandono
  // reconciliado como finalización falsearía el embudo justo al revés.
  const dismissedHere = sealed(dismissedVersion);
  const closedHere = completedHere || dismissedHere;

  return useMemo(() => ({
    isLoading,
    status,
    snoozed,
    closedHere,
    dismissedHere,
    // Separado de `active` a propósito: un recorrido aparcado sigue siendo
    // elegible, y es justo la combinación que busca `useOnboardingResume`.
    eligible: !!ob?.eligible,
    // «Activo» = hay que enseñarle la card AHORA. Un recorrido completado o
    // abandonado ya no se pinta —ninguno de los dos está en `OPEN`—; uno
    // aparcado tampoco, pero solo durante esta sesión.
    active: !!ob?.eligible && OPEN.has(status) && !snoozed && !closedHere,
    // El paso guardado se conserva aunque esté aparcado: el stepper tiene que
    // reabrirse donde lo dejó, no en el uno.
    step: ob?.step || 1,
    // La tanda del recorrido. Sella la marca local de terminado, para que subir
    // `ONBOARDING_VERSION` reabra el recorrido también a quien ya lo cerró.
    version: ob?.version ?? null,
    // Los pasos con tick. NO se deduce de `step`: la posición dice hasta dónde
    // se ha llegado, no por dónde se ha pasado, y saltar del 1 al 4 daba por
    // hechos el 2 y el 3. Lo manda el servidor (`steps_done`).
    stepsDone: Array.isArray(ob?.steps_done) ? ob.steps_done : [],
  }), [isLoading, ob, status, snoozed, closedHere, dismissedHere]);
}

/**
 * La vuelta atrás de «Guardar y seguir luego», para el menú de usuario.
 *
 * Aparcar el recorrido lo esconde hasta la próxima sesión, que es lo pedido,
 * pero deja a quien lo pulsó sin ninguna forma de recuperarlo ANTES: recargar
 * no basta, porque `sessionStorage` sobrevive a la recarga. Había que cerrar la
 * pestaña. Esto es esa salida, y por eso solo aparece cuando de verdad hay algo
 * que recuperar: recorrido elegible, todavía abierto y aparcado en esta sesión.
 *
 * No llama al servidor. Aparcar tampoco cambió nada allí —solo dejó la huella
 * de `snoozed_at`/`snooze_count`— así que desaparcar es borrar la clave local y
 * nada más. Y NO se descuenta del `snooze_count`: ese contador mide cuánta
 * gente pospone el recorrido, y restarle las vueltas atrás mezclaría dos
 * preguntas distintas.
 *
 * `page` sale con el estado y no del `resume`: quien lo pinta lo usa como
 * `<Link to>`, así que el enlace se comporta como un enlace de verdad (abrir en
 * otra pestaña, ver el destino al pasar por encima) en vez de como un botón que
 * navega por su cuenta.
 *
 * @returns {{canResume: boolean, page: string, resume: () => void}}
 */
export function useOnboardingResume() {
  const { eligible, status, snoozed, step, closedHere } = useOnboardingState();
  const resume = useCallback(() => clearSnoozed(), []);

  const page = useMemo(
    () => (STEP_PAGES.find((s) => s.order === step) || STEP_PAGES[0]).page,
    [step],
  );

  return {
    // `closedHere` aparte del estado del servidor: quien terminó el recorrido
    // con el POST caído lo tiene aún `in_progress` allí, y sin esto le saldría
    // un «Volver al recorrido» a algo que para él ya no existe.
    canResume: eligible && OPEN.has(status) && snoozed && !closedHere,
    page,
    resume,
  };
}

/**
 * Reconcilia un cierre que el servidor se perdió. Se monta UNA vez, con el
 * stepper.
 *
 * Si el POST de «Terminar» cayó, el usuario tiene el recorrido cerrado en su
 * navegador y el servidor lo sigue viendo abierto. Para el usuario da igual —la
 * marca local manda— pero el embudo de admin perdería esa finalización para
 * siempre, y con ella la mediana de tiempo hasta completar.
 *
 * Se reintenta UNA vez por carga y nunca más: el `ref` es lo que impide que un
 * fallo permanente (una cuota agotada) se convierta en un POST por render. En
 * el servidor la operación es idempotente —el guard sobre `completed_at` deja
 * el primer cierre intacto—, así que repetirla no falsea ninguna métrica.
 */
export function useOnboardingHeal() {
  const { eligible, status, closedHere, dismissedHere, version } = useOnboardingState();
  const { complete, dismiss } = useOnboardingActions();
  const healed = useRef(false);

  useEffect(() => {
    if (healed.current) return;
    // Solo si el servidor todavía lo ve abierto. Si ya está cerrado allí, no
    // hay nada que reconciliar.
    if (!closedHere || !eligible || !OPEN.has(status)) return;
    healed.current = true;
    // El abandono manda sobre la finalización: son excluyentes en la práctica
    // —después de cualquiera de los dos la tarjeta ya no está— pero si las dos
    // marcas coexistieran, apuntar «completado» convertiría un rechazo en un
    // éxito, que es el error que más caro sale de los dos.
    //
    // Sin paso: el sitio donde estaba al cerrar se perdió con la recarga, y
    // preferimos un abandono sin imputar a uno imputado al paso equivocado.
    if (dismissedHere) dismiss(null, version);
    else complete(version);
  }, [closedHere, dismissedHere, eligible, status, version, complete, dismiss]);
}

/**
 * Datos reales del paso `step`. Solo se piden si el recorrido está activo Y
 * `ready` — que es la palanca con la que cada página evita la carrera contra
 * su propia consulta de datos.
 *
 * @param {number} step
 * @param {{ready?: boolean}} [opts] `ready:false` deja la consulta apagada.
 */
export function useOnboardingStep(step, { ready = true } = {}) {
  const { active, status } = useOnboardingState();
  const here = useOnboardingHere();
  // Contra la PÁGINA y no contra el paso guardado. Cada página pinta su propia
  // card (`<OnboardingStepCard step={2} />` solo vive en Métricas), así que
  // esto es «estoy en la página de este paso» — y volver a un paso que se dejó
  // atrás vuelve a enseñar su card, que es justo lo que se espera al pulsarlo
  // en la lista.
  const onThisStep = active && here === step;

  const query = useQuery({
    queryKey: [...ONBOARDING_KEY, 'step', step],
    queryFn: () => getGbusinessOnboarding(step),
    enabled: onThisStep && ready,
    retry: false,
    staleTime: 60_000,
    // El paso del Escáner SEO puede llegar con su escaneo de cortesía todavía
    // en vuelo. Se repregunta cada 8 s SOLO mientras siga pendiente; el resto
    // de pasos nunca entra aquí porque nunca traen `pending`.
    refetchInterval: (q) => {
      const row = (q.state.data?.steps || []).find((s) => s.order === step);
      return row?.pending ? 8_000 : false;
    },
  });

  const row = useMemo(
    () => (query.data?.steps || []).find((s) => s.order === step) || null,
    [query.data, step],
  );

  return {
    visible: onThisStep,
    status,
    // `null` con `computed` = no hay datos suficientes → copy sin cifras.
    data: row?.data ?? null,
    computed: !!row?.computed,
    pending: !!row?.pending,
    isLoading: query.isLoading,
  };
}

/** Las acciones del recorrido, con actualización optimista del estado en `me`. */
export function useOnboardingActions() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ action, step }) => setGbusinessOnboarding(action, step),
    onMutate: async ({ action, step }) => {
      await queryClient.cancelQueries({ queryKey: ME_KEY });
      const previous = queryClient.getQueryData(ME_KEY);
      queryClient.setQueryData(ME_KEY, (old) => {
        if (!old?.onboarding) return old;
        const ob = { ...old.onboarding };
        // `snooze` no aparece aquí: no cambia nada del estado del servidor.
        if (action === 'start') ob.status = 'in_progress';
        if (action === 'advance') {
          ob.step = Math.max(ob.step || 1, step);
          // El tick tiene que salir al pulsar, igual que el avance. `advance`
          // con un paso MENOR que el guardado no mueve la posición pero sí
          // añade el tick: es el caso de volver a un paso que se saltó.
          const done = Array.isArray(ob.steps_done) ? ob.steps_done : [];
          ob.steps_done = done.includes(step) ? done : [...done, step].sort((a, b) => a - b);
        }
        if (action === 'complete') { ob.status = 'completed'; ob.step = TOTAL_STEPS; }
        // No se toca `step`: al abandonar, la posición se queda donde estaba.
        if (action === 'dismiss') ob.status = 'dismissed';
        return { ...old, onboarding: ob };
      });
      return { previous };
    },
    onError: (_e, _vars, context) => {
      // Se deshace en silencio: es progreso de una interfaz, no un dato del
      // negocio. Molestar con un toast por no haber podido apuntar el paso 3
      // sería peor que volver a enseñárselo.
      //
      // Incluido `complete`, y no por descuido: da igual lo que se haga aquí,
      // porque el `onSettled` invalida y el refetch trae el `in_progress` que
      // el servidor sigue teniendo. Quien impide que el recorrido reaparezca
      // es la marca local (`closedHere`), no esta línea.
      if (context?.previous) queryClient.setQueryData(ME_KEY, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });

  const { mutate } = mutation;

  const snooze = useCallback(() => {
    // El escondite es local y va PRIMERO: la card tiene que desaparecer al
    // pulsar, sin esperar a la red. La llamada es solo telemetría, y si falla
    // no cambia nada de lo que ve el usuario.
    writeSnoozed();
    mutate({ action: 'snooze' });
  }, [mutate]);

  return {
    /**
     * Marca el recorrido como empezado. Se dispara al ENSEÑARLO, no al pulsar
     * nada: es lo que separa «elegibles» de «empezados» en el embudo de admin,
     * y sin esta llamada quien viera la card y no avanzara nunca contaría como
     * si jamás se le hubiera enseñado. Idempotente en el servidor.
     */
    start: useCallback(() => mutate({ action: 'start' }), [mutate]),
    /** Avanza al paso `step`. El servidor ignora un paso menor que el guardado. */
    advance: useCallback((step) => mutate({ action: 'advance', step }), [mutate]),
    /**
     * Cierra el recorrido. La marca local va PRIMERO y por el mismo motivo que
     * en `snooze`: terminar tiene que surtir efecto al pulsar, sin depender de
     * que la red conteste.
     */
    complete: useCallback((version) => {
      writeCompleted(version);
      mutate({ action: 'complete' });
    }, [mutate]),
    /** «Guardar y seguir luego»: se esconde hasta la próxima sesión. */
    snooze,
    /**
     * La X: cierra el recorrido PARA SIEMPRE y lo deja escrito en Mongo.
     *
     * `step` es dónde estaba el usuario al cerrar —el paso que no le
     * convenció—, y no se puede sacar del estado guardado, que es la posición
     * más lejana alcanzada. Lo manda quien pinta la X, que es el único que sabe
     * en qué página está.
     *
     * La marca local va primero, igual que en `complete` y por lo mismo:
     * cerrar tiene que surtir efecto al pulsar, sin esperar a la red. Pero aquí
     * es solo un cinturón de seguridad — lo que de verdad impide que vuelva es
     * el `status: 'dismissed'` del servidor, y por eso funciona también en el
     * móvil del usuario.
     */
    dismiss: useCallback((step, version) => {
      writeDismissed(version);
      mutate({ action: 'dismiss', step: step ?? undefined });
    }, [mutate]),
    isPending: mutation.isPending,
  };
}
